/**
 * L'ÉDITEUR DE FIGURE — la console de conception, atelier de figures, lot L7
 * (AB#943 ; `docs/pilotage/atelier-figures/CONCEPTION.md` § 5.3 ; F13).
 *
 * « Les figures sont écrites à la main, en JSON, sans que l'auteur les voie. »
 * Ici, l'auteur CONSTRUIT, au navigateur, avec le moteur de l'atelier :
 *
 *  1. la figure de l'énoncé — il nomme les points, choisit le repère ;
 *  2. ce que l'élève en fera : la TRACER (la solution, dont l'éditeur propose
 *     les contraintes qu'elle tient — l'auteur coche ; les tracés fautifs,
 *     chacun rattaché à une erreur type ; une courbe peut se RÉGLER aux
 *     curseurs, L2) ou la DÉPLACER et observer (les points déplaçables et les
 *     mesures qui suivent, L2) ;
 *  3. la description pour qui ne voit pas, proposée par `decrireFigureV2` ;
 *  4. LES GRIEFS EN DIRECT (I-8 : une figure intraçable est refusée à
 *     l'écriture) — tant qu'il y en a un, rien ne sort ;
 *  5. LA SORTIE : le JSON de l'exercice, copié ou téléchargé, à verser dans
 *     `exercices/*.json` du chapitre. L'écriture en base reste la chaîne
 *     d'import existante (validation, vérification par calcul, import) : cet
 *     écran n'écrit RIEN au serveur.
 *
 * Aucun appel au modèle (I-11). Le projet se garde dans CE navigateur, comme
 * un brouillon : rien ne part tant que l'auteur ne copie pas.
 *
 * Tout ce qui se décide est dans `lib/editeur-figure-console.ts` et, pour ce
 * que l'import éprouve aussi, dans `editeur-figure.ts` du paquet partagé.
 */
import {
  CATEGORIES_EXERCICE,
  COMPETENCES_EXERCICE,
  OUTILS_TRACE,
  controlePropose,
  descriptionProposee,
  fichierDExercices,
  genreDeCible,
  outilsDeLaConstruction,
  proposerContraintes,
  resoudreConstruction,
  type CategorieExercice,
  type CompetenceExercice,
  type ContrainteProposee,
  type ContrainteRetenue,
  type FigureDynamique,
  type IdObjet,
  type NiveauExercice,
  type ObjetConstructionDto,
  type OutilTrace,
} from '@silex/shared';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ToileDeTrace } from '../components/FigureV2Web';
import { FormulaireCourbe } from '../components/relecture/TraceARelire';
import {
  AIDE_OUTIL,
  CLE_BROUILLON,
  ETAT_INITIAL,
  NOM_OUTIL_AUTEUR,
  OUTILS_DE_CONSTRUCTION,
  ajouterFautif,
  avecBac,
  bacDe,
  changerDeBranche,
  lireBrouillon,
  planDeLEtat,
  renommer,
  sortieDeLEtat,
  toucherEnAuteur,
  type Couche,
  type EtatEditeur,
  type OutilDeLAuteur,
} from '../lib/editeur-figure-console';
import {
  NOM_OUTIL,
  OUTILS_AU_CLIC,
  annulerDansLeBac,
  documentDuTrace,
  echelleDuTrace,
  fermerLePolygone,
  nomDansLeBac,
  poserDansLeBac,
  svgDuTrace,
  versFigure,
} from '../lib/relecture-trace';

import './relecture.css';

const RAYON_AIMANT_PX = 14;

const memeCouche = (a: Couche, b: Couche): boolean =>
  typeof a === 'string' || typeof b === 'string' ? a === b : a.fautif === b.fautif;

const nomCouche = (c: Couche): string =>
  c === 'figure' ? 'Figure de l’énoncé' : c === 'solution' ? 'Solution' : `Tracé fautif ${c.fautif + 1}`;

function lireDepuisLeNavigateur(): EtatEditeur {
  try {
    return lireBrouillon(globalThis.localStorage?.getItem(CLE_BROUILLON) ?? null) ?? ETAT_INITIAL;
  } catch {
    return ETAT_INITIAL;
  }
}

/* ─────────────────────────── la toile d'une couche ─────────────────────────── */

function ToileDeCouche({
  etat,
  couche,
  onChange,
}: {
  readonly etat: EtatEditeur;
  readonly couche: Couche;
  readonly onChange: (e: EtatEditeur) => void;
}): ReactNode {
  const [outil, setOutil] = useState<OutilDeLAuteur | 'fonction'>('point');
  const plan = useMemo(() => planDeLEtat(etat), [etat]);
  /* La figure se construit sur une page vierge ; la solution et les fautifs, SUR elle. */
  const t = useMemo(
    () => (couche === 'figure' ? { repere: plan.repere, enonce: null } : plan),
    [couche, plan],
  );
  const bac = bacDe(etat, couche);
  const sousLeTrace = typeof couche === 'object' ? etat.solution.objets : null;
  const svg = useMemo(
    () => svgDuTrace(t, bac.objets, sousLeTrace, nomCouche(couche)),
    [t, bac.objets, sousLeTrace, couche],
  );
  const echelle = useMemo(() => echelleDuTrace(t), [t]);
  const changer = (b: typeof bac): void => onChange(avecBac(etat, couche, b));
  const outils: readonly (OutilDeLAuteur | 'fonction')[] = [
    ...OUTILS_AU_CLIC,
    ...(couche === 'figure' ? [] : OUTILS_DE_CONSTRUCTION),
    'fonction',
  ];
  return (
    <section className="tr-bac" aria-label={`Construire : ${nomCouche(couche)}`}>
      <div className="tr-outils" role="toolbar" aria-label="Outils">
        {outils.map((o) => (
          <button
            key={o}
            type="button"
            className={o === outil ? 'btn btn--sm' : 'btn btn--ghost btn--sm'}
            aria-pressed={o === outil}
            onClick={() => {
              setOutil(o);
              changer({ ...bac, enCours: [] });
            }}
            data-testid={`editeur-outil-${o}`}
          >
            {o in NOM_OUTIL_AUTEUR
              ? NOM_OUTIL_AUTEUR[o as keyof typeof NOM_OUTIL_AUTEUR]
              : NOM_OUTIL[o as OutilTrace]}
          </button>
        ))}
        <span className="tr-outils__sep" />
        <button type="button" className="btn btn--quiet btn--sm" onClick={() => changer(annulerDansLeBac(bac))}>
          Annuler
        </button>
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={() => changer({ objets: [], enCours: [], passe: [...bac.passe, bac.objets] })}
        >
          Effacer la couche
        </button>
      </div>
      {outil === 'fonction' ? (
        <FormulaireCourbe onPoser={(def) => changer(poserDansLeBac(bac, 'f', def))} />
      ) : (
        <p className="cell-sub">{AIDE_OUTIL[outil]}</p>
      )}
      <ToileDeTrace
        svg={svg}
        testid="editeur-toile"
        onToucher={
          outil === 'fonction'
            ? undefined
            : (aff, pixelsParUnite) =>
                changer(
                  toucherEnAuteur(
                    t,
                    bac,
                    outil,
                    versFigure(echelle, aff),
                    RAYON_AIMANT_PX / pixelsParUnite / echelle.kx,
                  ),
                )
        }
      />
      <div className="tr-pas">
        <span className="cell-sub" data-testid="editeur-etat-couche">
          {bac.objets.length} objet{bac.objets.length > 1 ? 's' : ''}
          {sousLeTrace !== null ? ' · pointillé : la solution' : ''}
        </span>
        {outil === 'polygone' && bac.enCours.length >= 3 ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => changer(fermerLePolygone(bac))}>
            Fermer le polygone
          </button>
        ) : null}
      </div>
    </section>
  );
}

/* ─────────────────────────── la liste des objets ─────────────────────────── */

function ListeDeCouche({
  etat,
  couche,
  onChange,
}: {
  readonly etat: EtatEditeur;
  readonly couche: Couche;
  readonly onChange: (e: EtatEditeur) => void;
}): ReactNode {
  const bac = bacDe(etat, couche);
  const plan = useMemo(() => planDeLEtat(etat), [etat]);
  const resolue = useMemo(
    () =>
      resoudreConstruction(
        documentDuTrace(couche === 'figure' ? { ...plan, enonce: null } : plan, bac.objets),
        couche === 'figure' ? null : plan.enonce,
      ),
    [bac.objets, couche, plan],
  );
  if (bac.objets.length === 0) return <p className="note-line">Aucun objet : construis sur la figure.</p>;
  const estCible = (id: IdObjet): boolean => etat.cibles.some((c) => c.objet === id);
  const basculerCible = (o: ObjetConstructionDto): void => {
    const genre = genreDeCible(resolue.objets.get(o.id));
    if (genre === null) return;
    onChange({
      ...etat,
      cibles: estCible(o.id)
        ? etat.cibles.filter((c) => c.objet !== o.id)
        : [...etat.cibles, { id: o.id, objet: o.id, genre }],
      contraintes: {},
    });
  };
  return (
    <ul className="rx-liste" data-testid="editeur-objets">
      {bac.objets.map((o) => {
        const genre = genreDeCible(resolue.objets.get(o.id));
        return (
          <li key={o.id} className="tr-formulaire">
            <span className="contenus-mono">{o.id}</span>
            <span>{o.def.type.replace('_', ' ')}</span>
            <label>
              Nom{' '}
              <input
                value={o.nom ?? ''}
                size={4}
                onChange={(e) => onChange(avecBac(etat, couche, renommer(bac, o.id, e.target.value)))}
                aria-label={`Nom de ${nomDansLeBac(o)}`}
              />
            </label>
            {o.def.type === 'intersection' ? (
              <button
                type="button"
                className="btn btn--quiet btn--sm"
                onClick={() => onChange(avecBac(etat, couche, changerDeBranche(bac, o.id)))}
              >
                L’autre point
              </button>
            ) : null}
            {couche === 'solution' && genre !== null ? (
              <label>
                <input
                  type="checkbox"
                  checked={estCible(o.id)}
                  onChange={() => basculerCible(o)}
                  data-testid={`editeur-cible-${o.id}`}
                />{' '}
                L’élève le trace ({genre})
              </label>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/* ─────────────────────────── les contraintes proposées ─────────────────────────── */

function ContraintesProposees({
  proposees,
  cochees,
  avecErreur,
  onCocher,
  onErreur,
  testid,
}: {
  readonly proposees: readonly ContrainteProposee[];
  readonly cochees: Readonly<Record<string, unknown>>;
  readonly avecErreur: Readonly<Record<string, ContrainteRetenue>> | null;
  readonly onCocher: (p: ContrainteProposee, oui: boolean) => void;
  readonly onErreur?: (p: ContrainteProposee, erreur: string) => void;
  readonly testid: string;
}): ReactNode {
  if (proposees.length === 0) {
    return (
      <p className="note-line" data-testid={testid}>
        Rien à proposer : désigne d’abord ce que l’élève trace, sur une construction lisible.
      </p>
    );
  }
  return (
    <ul className="rx-liste" data-testid={testid}>
      {proposees.map((p) => {
        const oui = p.cle in cochees;
        return (
          <li key={p.cle} className="tr-formulaire">
            <label>
              <input type="checkbox" checked={oui} onChange={(e) => onCocher(p, e.target.checked)} />{' '}
              {p.phrase}
            </label>
            {oui && avecErreur !== null && onErreur !== undefined ? (
              <label className="cell-sub">
                Si elle manque, l’erreur{' '}
                <input
                  value={avecErreur[p.cle]?.erreur ?? ''}
                  size={12}
                  placeholder="E-MILIEU"
                  onChange={(e) => onErreur(p, e.target.value.toUpperCase())}
                />
              </label>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/* ─────────────────────────── la page ─────────────────────────── */

function Champ({
  libelle,
  children,
}: {
  readonly libelle: string;
  readonly children: (id: string) => ReactNode;
}): ReactNode {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{libelle}</label>
      {children(id)}
    </div>
  );
}

export function EditeurFigurePage(): ReactNode {
  const [etat, setEtat] = useState<EtatEditeur>(lireDepuisLeNavigateur);
  const [couche, setCouche] = useState<Couche>('figure');
  const [copie, setCopie] = useState<'repos' | 'copie' | 'echec'>('repos');
  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(CLE_BROUILLON, JSON.stringify(etat));
    } catch {
      /* Un navigateur qui ne garde rien : le projet vit le temps de la page. */
    }
  }, [etat]);

  const c = etat.champs;
  const champ = <K extends keyof EtatEditeur['champs']>(k: K, v: EtatEditeur['champs'][K]): void =>
    setEtat((e) => ({ ...e, champs: { ...e.champs, [k]: v } }));
  const plan = useMemo(() => planDeLEtat(etat), [etat]);
  const sortie = useMemo(() => sortieDeLEtat(etat), [etat]);
  const proposeesSolution = useMemo(
    () => proposerContraintes(plan.repere, plan.enonce, etat.solution.objets, etat.cibles),
    [plan, etat.solution.objets, etat.cibles],
  );
  const description = useMemo(
    () => descriptionProposee({ repere: c.repere, figure: etat.figure.objets }),
    [c.repere, etat.figure.objets],
  );
  const json = useMemo(
    () => JSON.stringify(fichierDExercices(c.chapitre || 'CHAPITRE', sortie.exercice), null, 2),
    [sortie, c.chapitre],
  );
  const pointsNommes = useMemo(
    () => (plan.enonce?.objets ?? []).flatMap((o) => (o.type === 'point' && o.nom ? [o.nom] : [])),
    [plan.enonce],
  );
  const supports = useMemo(
    () =>
      (plan.enonce?.objets ?? []).flatMap((o, i) =>
        o.type === 'cercle' || o.type === 'segment' || o.type === 'droite'
          ? [{ rang: `e:${i}`, quoi: `${o.type} (e:${i})` }]
          : [],
      ),
    [plan.enonce],
  );
  const fonctionCible = etat.cibles.find((x) => x.genre === 'fonction');
  const defFonction = etat.solution.objets.find((o) => o.id === fonctionCible?.objet)?.def;
  const sansGrief = sortie.griefs.length === 0;

  const dynamique: FigureDynamique = c.dynamique ?? { points: [] };
  const poserDynamique = (d: FigureDynamique): void =>
    champ('dynamique', d.points.length === 0 && (d.mesures ?? []).length === 0 ? undefined : d);

  return (
    <div className="content__inner" data-testid="editeur-figure">
      <div className="page-head">
        <div>
          <h1>Éditeur de figure</h1>
          <p className="page-sub">
            Construire la figure d’un exercice, sa solution et ses tracés fautifs, avec le moteur de
            l’atelier. La sortie est le JSON du chapitre : elle entre par la chaîne d’import, jamais
            d’ici. Aucun modèle n’est appelé.
          </p>
        </div>
        <Link className="btn btn--ghost btn--sm" to="/contenus">
          Contenus
        </Link>
      </div>

      <div className="rx-detail__colonnes editeur-colonnes">
        {/* ── La figure ── */}
        <div>
          <div className="tr-outils" role="tablist" aria-label="Couches">
            {(['figure', ...(c.nature === 'trace' ? ['solution'] : [])] as Couche[])
              .concat(c.nature === 'trace' ? etat.fautifs.map((_, i) => ({ fautif: i })) : [])
              .map((k) => (
                <button
                  key={typeof k === 'string' ? k : `f${k.fautif}`}
                  type="button"
                  role="tab"
                  aria-selected={memeCouche(k, couche)}
                  className={memeCouche(k, couche) ? 'btn btn--sm' : 'btn btn--ghost btn--sm'}
                  onClick={() => setCouche(k)}
                  data-testid={`editeur-couche-${typeof k === 'string' ? k : `fautif-${k.fautif}`}`}
                >
                  {nomCouche(k)}
                </button>
              ))}
            {c.nature === 'trace' ? (
              <button
                type="button"
                className="btn btn--quiet btn--sm"
                onClick={() => {
                  setEtat((e) => ajouterFautif(e));
                  setCouche({ fautif: etat.fautifs.length });
                }}
                data-testid="editeur-ajouter-fautif"
              >
                + Tracé fautif
              </button>
            ) : null}
          </div>
          <ToileDeCouche etat={etat} couche={couche} onChange={setEtat} />
          <h3 className="rx-h3">Les objets — {nomCouche(couche)}</h3>
          <ListeDeCouche etat={etat} couche={couche} onChange={setEtat} />
        </div>

        {/* ── Ce que l'auteur déclare ── */}
        <div className="stack">
          <section className="tr-bloc" aria-label="L’exercice">
            <h3 className="rx-h3">L’exercice</h3>
            <div className="form-grid form-grid--serre">
              <Champ libelle="Chapitre">
                {(id) => (
                  <input id={id} value={c.chapitre} placeholder="MAT-3-AFF" onChange={(e) => champ('chapitre', e.target.value.toUpperCase())} data-testid="editeur-chapitre" />
                )}
              </Champ>
              <Champ libelle="Alias">
                {(id) => (
                  <input id={id} value={c.alias} placeholder="N1-1-01" onChange={(e) => champ('alias', e.target.value.toUpperCase())} data-testid="editeur-alias" />
                )}
              </Champ>
              <Champ libelle="Notion">
                {(id) => <input id={id} value={c.notion} onChange={(e) => champ('notion', e.target.value.toUpperCase())} />}
              </Champ>
              <Champ libelle="Niveau">
                {(id) => (
                  <select id={id} value={c.niveauExercice} onChange={(e) => champ('niveauExercice', Number(e.target.value) as NiveauExercice)}>
                    {[1, 2, 3, 4].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                )}
              </Champ>
              <Champ libelle="Compétence">
                {(id) => (
                  <select id={id} value={c.competence} onChange={(e) => champ('competence', e.target.value as CompetenceExercice)}>
                    {COMPETENCES_EXERCICE.filter((x) => x.startsWith('MAT')).map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                )}
              </Champ>
              <Champ libelle="Catégorie">
                {(id) => (
                  <select id={id} value={c.categorie} onChange={(e) => champ('categorie', e.target.value as CategorieExercice)}>
                    {CATEGORIES_EXERCICE.map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                )}
              </Champ>
              <Champ libelle="Minutes">
                {(id) => (
                  <input id={id} type="number" min={1} value={c.minutesEstimees} onChange={(e) => champ('minutesEstimees', Number(e.target.value))} />
                )}
              </Champ>
            </div>
            <Champ libelle="Énoncé (texte riche : les maths entre \( et \))">
              {(id) => (
                <textarea id={id} rows={2} value={c.enonce} onChange={(e) => champ('enonce', e.target.value)} data-testid="editeur-enonce" />
              )}
            </Champ>
            <fieldset className="tr-formulaire">
              <legend>Ce que l’élève fait de la figure</legend>
              <label>
                <input type="radio" checked={c.nature === 'trace'} onChange={() => champ('nature', 'trace')} /> Il trace la
                réponse
              </label>
              <label>
                <input
                  type="radio"
                  checked={c.nature === 'observer'}
                  onChange={() => {
                    champ('nature', 'observer');
                    setCouche('figure');
                  }}
                  data-testid="editeur-nature-observer"
                />{' '}
                Il déplace des points et observe
              </label>
            </fieldset>
          </section>

          <section className="tr-bloc" aria-label="Le repère">
            <h3 className="rx-h3">Le repère</h3>
            <div className="tr-formulaire">
              {(['xMin', 'xMax', 'yMin', 'yMax'] as const).map((k) => (
                <label key={k}>
                  {k}{' '}
                  <input
                    type="number"
                    value={c.repere[k]}
                    size={4}
                    onChange={(e) => champ('repere', { ...c.repere, [k]: Number(e.target.value) })}
                  />
                </label>
              ))}
              <label>
                pas{' '}
                <input
                  type="number"
                  value={typeof c.repere.pas === 'number' ? c.repere.pas : 1}
                  min={0.1}
                  step={0.5}
                  onChange={(e) => champ('repere', { ...c.repere, pas: Number(e.target.value) })}
                />
              </label>
              <label>
                <input type="checkbox" checked={c.repere.axes === true} onChange={(e) => champ('repere', { ...c.repere, axes: e.target.checked })} /> axes
              </label>
              <label>
                <input type="checkbox" checked={c.repere.grille === true} onChange={(e) => champ('repere', { ...c.repere, grille: e.target.checked })} /> quadrillage
              </label>
            </div>
          </section>

          {c.nature === 'trace' ? (
            <>
              <section className="tr-bloc" aria-label="Les contraintes">
                <h3 className="rx-h3">Ce que le vérificateur exigera</h3>
                <p className="cell-sub">
                  Coche, dans la couche « Solution », l’objet que l’élève trace. L’éditeur propose les
                  contraintes que la solution TIENT, mesurées par le vérificateur ; coche celles qui comptent.
                </p>
                <ContraintesProposees
                  proposees={proposeesSolution}
                  cochees={etat.contraintes}
                  avecErreur={etat.contraintes}
                  testid="editeur-contraintes"
                  onCocher={(p, oui) =>
                    setEtat((e) => {
                      const suite = { ...e.contraintes };
                      if (oui) suite[p.cle] = { contrainte: p.contrainte, erreur: '' };
                      else delete suite[p.cle];
                      return { ...e, contraintes: suite };
                    })
                  }
                  onErreur={(p, erreur) =>
                    setEtat((e) => ({
                      ...e,
                      contraintes: { ...e.contraintes, [p.cle]: { contrainte: p.contrainte, erreur } },
                    }))
                  }
                />
              </section>

              <section className="tr-bloc" aria-label="Les outils">
                <h3 className="rx-h3">Les outils offerts à l’élève</h3>
                <div className="tr-formulaire">
                  {OUTILS_TRACE.map((o) => (
                    <label key={o}>
                      <input
                        type="checkbox"
                        checked={c.outils.includes(o)}
                        onChange={(e) =>
                          champ('outils', e.target.checked ? [...c.outils, o] : c.outils.filter((x) => x !== o))
                        }
                      />{' '}
                      {NOM_OUTIL[o]}
                    </label>
                  ))}
                  <button
                    type="button"
                    className="btn btn--quiet btn--sm"
                    onClick={() =>
                      champ('outils', [...new Set([...c.outils, ...outilsDeLaConstruction(etat.solution.objets)])])
                    }
                  >
                    Ceux de la solution
                  </button>
                </div>
              </section>

              {fonctionCible !== undefined && defFonction?.type === 'fonction' ? (
                <section className="tr-bloc" aria-label="Réglable">
                  <h3 className="rx-h3">Réglable aux curseurs (mode Explorer)</h3>
                  <label>
                    <input
                      type="checkbox"
                      checked={c.reglage !== undefined}
                      onChange={(e) =>
                        champ(
                          'reglage',
                          e.target.checked
                            ? {
                                objet: fonctionCible.id,
                                famille: defFonction.famille,
                                curseurs: Object.keys(defFonction.parametres)
                                  .filter((k) => typeof defFonction.parametres[k] === 'number')
                                  .slice(0, 4)
                                  .map((nom) => ({ nom, min: -5, max: 5, pas: 0.5, depart: 0 })),
                              }
                            : undefined,
                        )
                      }
                    />{' '}
                    L’élève règle la courbe aux curseurs
                  </label>
                  {c.reglage !== undefined ? (
                    <ul className="rx-liste">
                      {c.reglage.curseurs.map((k, i) => (
                        <li key={k.nom} className="tr-formulaire">
                          <b>{k.nom}</b>
                          {(['min', 'max', 'pas', 'depart'] as const).map((cle) => (
                            <label key={cle}>
                              {cle}{' '}
                              <input
                                type="number"
                                value={k[cle]}
                                step={0.5}
                                size={4}
                                onChange={(e) => {
                                  const r = c.reglage;
                                  if (r === undefined) return;
                                  champ('reglage', {
                                    ...r,
                                    curseurs: r.curseurs.map((x, j) => (j === i ? { ...x, [cle]: Number(e.target.value) } : x)),
                                  });
                                }}
                              />
                            </label>
                          ))}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ) : null}

              {etat.fautifs.map((f, i) => (
                <FautifDeLAuteur key={i} etat={etat} i={i} onChange={setEtat} />
              ))}

              <section className="tr-bloc" aria-label="Le contrôle par calcul">
                <h3 className="rx-h3">Le contrôle par calcul (recalculé par l’import)</h3>
                <div className="tr-formulaire">
                  <input value={c.controle} size={36} onChange={(e) => champ('controle', e.target.value)} data-testid="editeur-controle" />
                  <button
                    type="button"
                    className="btn btn--quiet btn--sm"
                    onClick={() => champ('controle', controlePropose(plan.repere, plan.enonce, etat.solution.objets, etat.cibles[0]))}
                  >
                    Proposer
                  </button>
                </div>
              </section>
            </>
          ) : (
            <section className="tr-bloc" aria-label="Déplaçable">
              <h3 className="rx-h3">Déplaçable : ce que l’élève fait bouger</h3>
              {pointsNommes.length === 0 ? (
                <p className="note-line">Nomme d’abord les points de la figure.</p>
              ) : (
                <ul className="rx-liste" data-testid="editeur-deplacables">
                  {pointsNommes.map((nom) => {
                    const decl = dynamique.points.find((p) => p.nom === nom);
                    return (
                      <li key={nom} className="tr-formulaire">
                        <label>
                          <input
                            type="checkbox"
                            checked={decl !== undefined}
                            onChange={(e) =>
                              poserDynamique({
                                ...dynamique,
                                points: e.target.checked
                                  ? [...dynamique.points, { nom }]
                                  : dynamique.points.filter((p) => p.nom !== nom),
                              })
                            }
                            data-testid={`editeur-deplacable-${nom}`}
                          />{' '}
                          {nom}
                        </label>
                        {decl !== undefined ? (
                          <label>
                            reste sur{' '}
                            <select
                              value={decl.sur ?? ''}
                              onChange={(e) =>
                                poserDynamique({
                                  ...dynamique,
                                  points: dynamique.points.map((p) =>
                                    p.nom === nom ? (e.target.value === '' ? { nom } : { nom, sur: e.target.value }) : p,
                                  ),
                                })
                              }
                            >
                              <option value="">(libre)</option>
                              {supports.map((s) => (
                                <option key={s.rang} value={s.rang}>
                                  {s.quoi}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
              <MesuresDynamiques points={pointsNommes} dynamique={dynamique} onChange={poserDynamique} />
              <Champ libelle="Résultat attendu (ce qui reste vrai)">
                {(id) => <textarea id={id} rows={2} value={c.resultatAttendu} onChange={(e) => champ('resultatAttendu', e.target.value)} />}
              </Champ>
              <div className="tr-formulaire">
                <label>
                  Contrôle (calcul){' '}
                  <input value={c.controle} onChange={(e) => champ('controle', e.target.value)} placeholder="acos(0)*180/pi" />
                </label>
                <label>
                  Résultat{' '}
                  <input value={c.resultatControle} onChange={(e) => champ('resultatControle', e.target.value)} placeholder="90" />
                </label>
              </div>
            </section>
          )}

          <section className="tr-bloc" aria-label="La description">
            <h3 className="rx-h3">Ce que la figure montre, pour qui ne la voit pas</h3>
            <textarea
              rows={3}
              value={c.decrit === '' ? description : c.decrit}
              onChange={(e) => champ('decrit', e.target.value)}
              data-testid="editeur-decrit"
              aria-label="Description de la figure"
            />
            <p className="cell-sub">
              Proposée par la description du moteur ; à relire par un humain.{' '}
              <button type="button" className="btn btn--quiet btn--sm" onClick={() => champ('decrit', '')}>
                Reprendre la proposition
              </button>
            </p>
          </section>

          <section className="tr-bloc" aria-label="La démarche de référence">
            <h3 className="rx-h3">La démarche de référence (deux étapes au moins)</h3>
            {c.demarcheReference.map((s, i) => (
              <div key={i} className="tr-formulaire">
                <input
                  value={s.ecriture}
                  size={28}
                  placeholder="Écriture"
                  onChange={(e) => champ('demarcheReference', c.demarcheReference.map((x, j) => (j === i ? { ...x, ecriture: e.target.value } : x)))}
                />
                <input
                  value={s.justification}
                  size={28}
                  placeholder="Justification"
                  onChange={(e) => champ('demarcheReference', c.demarcheReference.map((x, j) => (j === i ? { ...x, justification: e.target.value } : x)))}
                />
              </div>
            ))}
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              onClick={() => champ('demarcheReference', [...c.demarcheReference, { ecriture: '', justification: '' }])}
            >
              + Étape
            </button>
          </section>

          <section className="tr-bloc" aria-label="Les griefs" data-testid="editeur-griefs">
            <h3 className="rx-h3">Ce qui empêche l’exercice d’entrer</h3>
            {sansGrief ? (
              <p className="note-line" data-testid="editeur-sans-grief">
                Aucun grief : la figure se dessine, la solution est jugée juste
                {etat.fautifs.length > 0 ? ', chaque tracé fautif déclenche son erreur' : ''}.
              </p>
            ) : (
              <ul className="rx-liste">
                {sortie.griefs.map((g) => (
                  <li key={g} className="tr-alerte">
                    {g}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="tr-bloc" aria-label="La sortie">
            <h3 className="rx-h3">La sortie : le fichier du chapitre</h3>
            <p className="cell-sub">
              À verser dans <span className="contenus-mono">exercices/</span> du dossier du chapitre,
              puis : <span className="contenus-mono">verifier-exercices.py</span>, la validation et l’import.
            </p>
            <div className="tr-pas">
              <button
                type="button"
                className="btn btn--sm"
                disabled={!sansGrief}
                data-testid="editeur-copier"
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(json)
                    .then(() => setCopie('copie'))
                    .catch(() => setCopie('echec'));
                }}
              >
                Copier le JSON
              </button>
              <a
                className={sansGrief ? 'btn btn--ghost btn--sm' : 'btn btn--ghost btn--sm is-disabled'}
                aria-disabled={!sansGrief}
                href={sansGrief ? `data:application/json;charset=utf-8,${encodeURIComponent(json)}` : undefined}
                download={`${c.chapitre || 'CHAPITRE'}-${c.alias || 'exercice'}.json`}
                data-testid="editeur-telecharger"
              >
                Télécharger
              </a>
              {copie !== 'repos' ? (
                <span className="cell-sub" role="status">
                  {copie === 'copie' ? 'Copié.' : 'La copie a échoué : sélectionne le texte.'}
                </span>
              ) : null}
            </div>
            <textarea className="editeur-json" readOnly rows={12} value={json} data-testid="editeur-json" aria-label="Le JSON du chapitre" />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── un tracé fautif ─────────────────────────── */

function FautifDeLAuteur({
  etat,
  i,
  onChange,
}: {
  readonly etat: EtatEditeur;
  readonly i: number;
  readonly onChange: (f: (e: EtatEditeur) => EtatEditeur) => void;
}): ReactNode {
  const f = etat.fautifs[i];
  const plan = useMemo(() => planDeLEtat(etat), [etat]);
  const proposees = useMemo(
    () => (f === undefined ? [] : proposerContraintes(plan.repere, plan.enonce, f.bac.objets, etat.cibles)),
    [plan, f, etat.cibles],
  );
  const sortie = useMemo(() => sortieDeLEtat(etat), [etat]);
  if (f === undefined) return null;
  const poser = (patch: Partial<typeof f>): void =>
    onChange((e) => ({ ...e, fautifs: e.fautifs.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const declenche = sortie.fautifs[i]?.declenche ?? false;
  return (
    <section className="tr-bloc" aria-label={`Tracé fautif ${i + 1}`} data-testid={`editeur-fautif-${i}`}>
      <h3 className="rx-h3">
        Tracé fautif {i + 1}{' '}
        <span className={declenche ? 'badge badge--acquis' : 'badge badge--a-revoir'}>
          {declenche ? 'Déclenche son erreur' : 'Ne déclenche pas son erreur'}
        </span>
      </h3>
      <div className="tr-formulaire">
        <input value={f.reponse} size={30} placeholder="Libellé du tracé fautif" onChange={(e) => poser({ reponse: e.target.value })} />
        <input value={f.erreur} size={12} placeholder="E-ERREUR" onChange={(e) => poser({ erreur: e.target.value.toUpperCase() })} />
      </div>
      <input value={f.cheminement} size={60} placeholder="Comment l’élève arrive à ce tracé" onChange={(e) => poser({ cheminement: e.target.value })} />
      <p className="cell-sub">Ce qu’il tient (coche ce qui le distingue) :</p>
      <ContraintesProposees
        proposees={proposees}
        cochees={f.contraintes}
        avecErreur={null}
        testid={`editeur-fautif-${i}-contraintes`}
        onCocher={(p, oui) => {
          const suite = { ...f.contraintes };
          if (oui) suite[p.cle] = p.contrainte;
          else delete suite[p.cle];
          poser({ contraintes: suite });
        }}
      />
      <div className="tr-formulaire">
        <label>
          Contrôle{' '}
          <input value={f.controle} size={24} onChange={(e) => poser({ controle: e.target.value })} />
        </label>
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={() => poser({ controle: controlePropose(plan.repere, plan.enonce, f.bac.objets, etat.cibles[0]) })}
        >
          Proposer
        </button>
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={() => onChange((e) => ({ ...e, fautifs: e.fautifs.filter((_, j) => j !== i) }))}
        >
          Retirer ce tracé fautif
        </button>
      </div>
    </section>
  );
}

/* ─────────────────────────── les mesures qui suivent ─────────────────────────── */

function MesuresDynamiques({
  points,
  dynamique,
  onChange,
}: {
  readonly points: readonly string[];
  readonly dynamique: FigureDynamique;
  readonly onChange: (d: FigureDynamique) => void;
}): ReactNode {
  const mesures = dynamique.mesures ?? [];
  const [sorte, setSorte] = useState<'angle' | 'longueur'>('angle');
  const [choix, setChoix] = useState<readonly string[]>([points[0] ?? '', points[1] ?? '', points[2] ?? '']);
  if (points.length < 2) return null;
  const roles = sorte === 'angle' ? (['de', 'sommet', 'a'] as const) : (['de', 'a'] as const);
  const ajouter = (): void => {
    const [x, y, z] = choix as [string, string, string];
    onChange({
      ...dynamique,
      mesures: [
        ...mesures,
        sorte === 'angle' ? { type: 'angle', de: x, sommet: y, a: z } : { type: 'longueur', de: x, a: y },
      ],
    });
  };
  return (
    <div data-testid="editeur-mesures">
      <p className="cell-sub">Les mesures qui se mettent à jour quand l’élève déplace un point :</p>
      <ul className="rx-liste">
        {mesures.map((m, i) => (
          <li key={i} className="tr-formulaire">
            {m.type === 'angle'
              ? `L’angle ${m.de}${m.sommet}${m.a}`
              : m.type === 'longueur'
                ? `La longueur ${m.de}${m.a}`
                : `Les coordonnées de ${m.point}`}
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              onClick={() => onChange({ ...dynamique, mesures: mesures.filter((_, j) => j !== i) })}
            >
              Retirer
            </button>
          </li>
        ))}
      </ul>
      <div className="tr-formulaire">
        <select value={sorte} onChange={(e) => setSorte(e.target.value as 'angle' | 'longueur')} aria-label="Sorte de mesure">
          <option value="angle">Angle</option>
          <option value="longueur">Longueur</option>
        </select>
        {roles.map((role, k) => (
          <select
            key={role}
            value={choix[k] ?? ''}
            aria-label={role === 'sommet' ? 'Sommet' : `Point ${k + 1}`}
            onChange={(e) => setChoix(choix.map((x, j) => (j === k ? e.target.value : x)))}
          >
            {points.map((nom) => (
              <option key={nom} value={nom}>
                {role === 'sommet' ? `sommet ${nom}` : nom}
              </option>
            ))}
          </select>
        ))}
        <button type="button" className="btn btn--quiet btn--sm" onClick={ajouter}>
          + Mesure
        </button>
      </div>
    </div>
  );
}
